import { Divider } from "@freightsimple/ui-core/ComponentLayout/Divider";
import { Row } from "@freightsimple/ui-core/ComponentLayout/Row";
import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";
import { T } from "@freightsimple/ui-core/Typography/T";
import SlideDown from "@/Shared/Animations/CustomComponents/SlideDown";
import type { FreightClaim } from "@freightsimple/generated-dashboard-openapi-client/models/FreightClaim";
import { FreightClaimType } from "@freightsimple/generated-dashboard-openapi-client/models/FreightClaimType";
import { RevealToggle } from "@/Shared/Layout/CustomComponents/RevealBox";
import { ShipmentInfoColumn } from "@/Shared/Shipment/CustomComponents/ShipmentInfoColumn";
import type { ReactNode } from "react";
import { useState } from "react";
import moment from "moment";
import { capitalize } from "lodash";
import { calculateTotalFreightClaim } from "@/Helpers/calculateTotalFreightClaim";
import { ViewShipmentsAffectedFreightClaimItem } from "./ViewShipmentsAffectedFreightClaimItem";

import { ViewShipmentsFreightClaimRowDocumentsTable } from "../Tables/ViewShipmentsFreightClaimRowDocumentsTable";
import type { DocumentsSectionItem } from "@/Shared/Shipment/CustomComponents/ShipmentDocumentsSectionGeneric";

interface FreightClaimSectionRowProps {
  freightClaim: FreightClaim;
  documents: DocumentsSectionItem[];
  last: boolean;
  disableReveal: boolean;
}

/**
 * One claim on the shipment page: a row of headline figures, and the detail
 * that reveals under it.
 *
 * The detail used to sit in its own bordered box. Inside the Claims section's
 * card that was a frame within a frame, and because the box animates its own
 * height with `overflow: hidden`, its bottom edge cut across the content
 * mid-reveal — the "weird border" in ENG-6743. A rule does the same job of
 * saying "this belongs to the row above" without drawing a second card.
 */
// oxlint-disable-next-line complexity -- grandfathered, see ENG-7672
export function ViewShipmentsFreightClaimSectionRow({
  freightClaim,
  last = false,
  disableReveal = false,
  documents,
}: FreightClaimSectionRowProps) {
  const [open, setOpen] = useState(disableReveal);
  const date = moment(freightClaim.createdAt);
  const damagedItems =
    freightClaim.freightClaimLineItems?.filter(
      (item) => item.claimType === FreightClaimType.Damage,
    ) ?? [];

  const lostItems =
    freightClaim.freightClaimLineItems?.filter(
      (item) => item.claimType === FreightClaimType.Loss,
    ) ?? [];

  const totalLostPieces = lostItems.reduce(
    (acc, item) => acc + (item.piecesAffected ?? 0),
    0,
  );

  const totalDamagedPieces = damagedItems.reduce(
    (acc, item) => acc + (item.piecesAffected ?? 0),
    0,
  );

  const totalFilesCount =
    (freightClaim.originalInvoiceFiles?.length ?? 0) +
    (freightClaim.otherDocumentFiles?.length ?? 0) +
    (freightClaim.photoFiles?.length ?? 0);

  return (
    <>
      <Stack gap={4} align="stretch">
        <Row justify="between" align="center" gap={4}>
          {/* A wrapping row, not the fixed pixel grid this used to be: the
              columns vary per claim (lost, damaged, or both), so a hard-coded
              template had to be recomputed from the data to stay in step. */}
          <Row gap={6} align="start" wrap shrinkable>
            <ShipmentInfoColumn
              label="Status"
              value={capitalize(freightClaim.freightClaimState)}
            />
            <ShipmentInfoColumn
              label="Submitted"
              value={date.format("dddd Do MMMM YYYY")}
            />
            {freightClaim.freightClaimLineItems && (
              <ShipmentInfoColumn
                label="Total Amount"
                value={
                  <>
                    $
                    {calculateTotalFreightClaim(
                      freightClaim.freightClaimLineItems,
                    )}{" "}
                    {freightClaim.claimCurrency}
                  </>
                }
              />
            )}
            <ShipmentInfoColumn label="Documents" value={totalFilesCount} />
            {damagedItems.length > 0 && (
              <ShipmentInfoColumn
                label="Damaged Pieces"
                value={totalDamagedPieces}
              />
            )}
            {lostItems.length > 0 && (
              <ShipmentInfoColumn label="Lost Pieces" value={totalLostPieces} />
            )}
          </Row>
          {!disableReveal && (
            <RevealToggle
              open={open}
              onToggle={() => setOpen((prev) => !prev)}
            />
          )}
        </Row>
        <SlideDown show={open}>
          <Stack gap={4} align="stretch">
            <Divider />
            <DetailBlock_ label="Details">
              <Row gap={6} align="start" wrap>
                {[...damagedItems, ...lostItems].map((item) => (
                  <ViewShipmentsAffectedFreightClaimItem
                    key={`${item.claimType}-${item.description}-${item.piecesAffected}`}
                    item={item}
                    freightClaim={freightClaim}
                  />
                ))}
              </Row>
            </DetailBlock_>
            <DetailBlock_ label="Description">
              <T color="secondary">{freightClaim.claimDescription}</T>
            </DetailBlock_>
            {freightClaim.claimInternalNotes && (
              <DetailBlock_ label="Other Notes">
                <T color="secondary">{freightClaim.claimInternalNotes}</T>
              </DetailBlock_>
            )}
            <ViewShipmentsFreightClaimRowDocumentsTable
              documents={documents}
              freightClaim={freightClaim}
            />
          </Stack>
        </SlideDown>
      </Stack>
      {!last && <Divider spacing="lg" />}
    </>
  );
}

/**
 * A labelled block of the revealed detail. The label matches the weight and
 * colour of the headline columns above it, so the whole card reads as one
 * scale rather than the label shrinking and dimming the further down you go.
 */
function DetailBlock_(props: { label: string; children: ReactNode }) {
  return (
    <Stack gap={0} align="start">
      <T size="base" weight="light">
        {props.label}
      </T>
      {props.children}
    </Stack>
  );
}
